import React, { useEffect, useState, useRef } from 'react';
import { Handle, Position, useUpdateNodeInternals, NodeProps } from 'reactflow';
import { Box, Typography, Tooltip } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';

interface SupervisorNodeData {
  label: string;
  instructions?: string;
}

const SupervisorNode: React.FC<NodeProps<SupervisorNodeData>> = ({ data = { label: 'Supervisor' }, id }) => {
  const updateNodeInternals = useUpdateNodeInternals();
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (id) {
      updateNodeInternals(id);
    }
  }, [id, updateNodeInternals]);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [data.label]);

  return (
    <Box
      sx={{
        width: 140,
        height: 40,
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid #80BFFF',
        px: 1,
        outline: 'none',
        '&:focus': {
          outline: 'none',
        },
        '&:hover': {
          outline: 'none',
        },
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          backgroundColor: '#3B82F6',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 1,
        }}
      >
        <StarIcon sx={{ color: '#ffffff', fontSize: 10 }} />
      </Box>
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Tooltip title={isTruncated ? data.label : ''} arrow placement="top" disableHoverListener={!isTruncated}>
          <Typography ref={textRef} variant="body2" sx={{ fontWeight: 500, fontSize: '9px', lineHeight: 1.2, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '75px' }}>
            {data.label}
          </Typography>
        </Tooltip>
        {data.instructions && (
          <Typography variant="caption" sx={{ fontSize: '7px', color: '#6B7280' }}>
            ✨ {data.instructions.substring(0, 20)}...
          </Typography>
        )}
      </Box>
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#000000', width: 5, height: 5 }}
      />

      <Handle
        type="source"
        position={Position.Right}
        id="agents"
        style={{
          background: '#000000',
          width: 5,
          height: 5,
          top: '20%',
        }}
      />

      <Handle
        type="source"
        position={Position.Right}
        id="output"
        style={{
          background: '#000000',
          width: 5,
          height: 5,
          top: '80%',
        }}
      />

      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          right: 6,
          top: '13%',
          fontSize: '5px',
          color: '#6B7280',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
      >
        Agents
      </Typography>

      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          right: 6,
          top: '73%',
          fontSize: '5px',
          color: '#6B7280',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
      >
        Output
      </Typography>
    </Box>
  );
};

export default SupervisorNode;
